/**
 * URL utility functions for handling return URLs and redirects
 */

/**
 * Extract return URL from current page URL parameters
 */
export const extractReturnUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('return_url');
};

/**
 * Store return URL securely in session storage
 */
export const storeReturnUrl = (returnUrl: string): void => {
  if (!returnUrl) return;
  
  try {
    // Use session storage for security - it's cleared when tab closes
    sessionStorage.setItem('auth_return_url', returnUrl);
  } catch (error) {
    console.warn('Failed to store return URL:', error);
  }
};

/**
 * Retrieve stored return URL from session storage
 */
export const getStoredReturnUrl = (): string | null => {
  try {
    return sessionStorage.getItem('auth_return_url');
  } catch (error) {
    console.warn('Failed to retrieve return URL:', error);
    return null;
  }
};

/**
 * Clear stored return URL from session storage
 */
export const clearStoredReturnUrl = (): void => {
  try {
    sessionStorage.removeItem('auth_return_url');
  } catch (error) {
    console.warn('Failed to clear return URL:', error);
  }
};

/**
 * Validate return URL against security rules
 */
export const isValidReturnUrl = (url: string): boolean => {
  if (!url) return false;
  
  try {
    const parsedUrl = new URL(url);
    
    // Basic security checks
    if (parsedUrl.protocol !== 'https:' && 
        !parsedUrl.hostname.includes('localhost') && 
        parsedUrl.hostname !== '127.0.0.1') {
      return false;
    }
    
    // Prevent javascript: and data: URLs
    if (parsedUrl.protocol === 'javascript:' || parsedUrl.protocol === 'data:') {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Sanitize return URL to prevent XSS attacks
 */
export const sanitizeReturnUrl = (url: string): string => {
  if (!url) return '';
  
  try {
    const parsedUrl = new URL(url);
    
    // Reconstruct URL to remove any potential malicious content
    return `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}`;
  } catch (error) {
    return '';
  }
};

/**
 * Build redirect URL with authentication token
 */
export const buildRedirectUrl = (returnUrl: string, token: string): string => {
  if (!returnUrl || !token) return returnUrl || '/';
  
  try {
    const url = new URL(returnUrl);
    url.searchParams.set('token', token);
    return url.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL
    return returnUrl;
  }
};

/**
 * Handle redirect after successful authentication
 */
export const handleAuthenticationRedirect = (token: string, returnUrl?: string, expiresAt?: string): void => {
  const targetUrl = returnUrl || getStoredReturnUrl();
  
  if (targetUrl && isValidReturnUrl(targetUrl)) {
    const sanitizedUrl = sanitizeReturnUrl(targetUrl);
    const redirectUrl = buildRedirectUrlWithExpiry(sanitizedUrl, token, expiresAt);
    
    // Clear stored return URL
    clearStoredReturnUrl();
    
    console.log('Redirecting to authenticated URL', { 
      targetUrl: sanitizedUrl,
      hasToken: !!token,
      hasExpiry: !!expiresAt
    });
    
    // Redirect to the target URL
    window.location.href = redirectUrl;
  } else {
    console.log('No valid return URL, redirecting to default dashboard');
    // Default redirect to dashboard or home page
    window.location.href = '/dashboard';
  }
};

/**
 * Build redirect URL with authentication token and expiry
 */
export const buildRedirectUrlWithExpiry = (returnUrl: string, token: string, expiresAt?: string): string => {
  if (!returnUrl || !token) return returnUrl || '/';
  
  try {
    const url = new URL(returnUrl);
    url.searchParams.set('token', token);
    
    if (expiresAt) {
      url.searchParams.set('expires_at', expiresAt);
    }
    
    return url.toString();
  } catch (error) {
    console.error('Failed to build redirect URL with expiry:', error);
    // If URL parsing fails, return the original URL
    return returnUrl;
  }
};

/**
 * Get current page URL with return URL parameter
 */
export const getCurrentUrlWithReturnUrl = (returnUrl: string): string => {
  const currentUrl = new URL(window.location.href);
  
  if (returnUrl) {
    currentUrl.searchParams.set('return_url', returnUrl);
  }
  
  return currentUrl.toString();
};

/**
 * Remove return URL parameter from current URL
 */
export const removeReturnUrlFromCurrentUrl = (): string => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete('return_url');
  return currentUrl.toString();
};

/**
 * Parse URL parameters into an object
 */
export const parseUrlParameters = (url?: string): Record<string, string> => {
  const targetUrl = url || window.location.href;
  const params: Record<string, string> = {};
  
  try {
    const urlObj = new URL(targetUrl);
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
  } catch (error) {
    console.warn('Failed to parse URL parameters:', error);
  }
  
  return params;
};